import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import {
    construirReporte,
    describirError,
    firmaError,
    LimitadorErrores,
    mensajeHttp,
} from './errores-cliente';

const CLAVE_CONTEO = 'tp_errores_reportados';

/**
 * Manda al back los errores de la web (`POST /monitoring/client-errors`) para
 * la alarma de errores. Nunca debe romper la app ni generar bucles:
 * - usa HttpBackend, asi el envio no pasa por los interceptores (ni el de
 *   errores HTTP, ni el que cierra sesion en 401, ni la barra de carga);
 * - todo va en try/catch y los errores del propio envio se tragan;
 * - una bandera evita que un error durante el reporte se reporte a si mismo.
 * La app Flutter no usa esto: tiene Crashlytics.
 */
@Injectable({ providedIn: 'root' })
export class ErroresClienteService {
    private readonly http = new HttpClient(inject(HttpBackend));
    private readonly url = `${environment.apiUrl}/monitoring/client-errors`;
    private readonly limitador = new LimitadorErrores(leerConteo());
    private reportando = false;

    /** Error de JavaScript que llego al ErrorHandler global. */
    reportarExcepcion(error: unknown): void {
        this.seguro(() => this.enviar(describirError(error)));
    }

    /** Falla HTTP 5xx/0 de la API (ya filtrada con esHttpReportable). */
    reportarHttp(status: number, metodo: string, url: string, requestId?: string | null): void {
        this.seguro(() => this.enviar({ mensaje: mensajeHttp(status, metodo, url, requestId) }));
    }

    private seguro(fn: () => void): void {
        if (this.reportando || !environment.apiUrl) return;
        this.reportando = true;
        try {
            fn();
        } catch {
            // El reporte nunca rompe la app.
        } finally {
            this.reportando = false;
        }
    }

    private enviar(datos: { mensaje: string; pila?: string }): void {
        const reporte = construirReporte(
            datos,
            typeof window !== 'undefined' ? window.location.pathname : '/',
            typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        );
        if (!this.limitador.permitir(firmaError(reporte.mensaje, reporte.ruta), Date.now())) return;
        guardarConteo(this.limitador.total);

        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        // JWT opcional: si hay sesion vigente el back solo anota quien fue.
        const token = leerToken();
        if (token) headers = headers.set('Authorization', 'Bearer ' + token);

        this.http.post(this.url, reporte, { headers }).subscribe({ error: () => {} });
    }
}

function leerConteo(): number {
    try {
        return Number(sessionStorage.getItem(CLAVE_CONTEO)) || 0;
    } catch {
        return 0;
    }
}

function guardarConteo(n: number): void {
    try {
        sessionStorage.setItem(CLAVE_CONTEO, String(n));
    } catch {
        // Sin almacenamiento el tope queda por carga de pagina.
    }
}

function leerToken(): string {
    try {
        const token = localStorage.getItem('accessToken') ?? '';
        return token && !AuthUtils.isTokenExpired(token) ? token : '';
    } catch {
        return '';
    }
}
